import React, { useMemo, Fragment } from "react";
import { useParams } from "react-router";
import styled, { css } from "styled-components";
import { textForStockItemsAmount } from "../stock";
import { useTooltip } from "../../components/Tooltip";
import { useTranslation, Trans } from "@coworker/locales";
import { parseProduct } from "../../services/products.service";
import { useAvailableElevatedStock } from "../../hooks/useAvailableElevatedStock";
import { LocationTag, Tag } from "../../components/LocationTag";
import { useItemsInfoQueries } from "@coworker/apprestructured/src/shared/hooks/item/useItemsInfo";
import { useGroupedFilteredLocations } from "../../components/InputPopup/Location";
import { useMFSLocations } from "../../hooks/Locations/useMFSLocations";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import { evaluateIsFoodArticle } from "../../helpers/utils";

const StockBadge = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--success);
  color: var(--white);
  height: 51px;
  padding: 0 28px;

  & span:first-child {
    font-size: 14px;
    font-weight: bold;
    line-height: 1.14;
  }

  & span:nth-child(2) {
    font-size: 14px;
    line-height: 1.71;
  }

  ${({ noStock }) =>
    noStock &&
    css`
      background: var(--error);
      color: var(--white);
    `}
  ${({ noInfo }) =>
    noInfo &&
    css`
      background: var(--grey500);
      color: var(--white);
    `}
`;

const ItemContainer = styled("div")`
  background: var(--white);
  margin-top: 10px;
`;

const ItemInfo = styled("div")`
  display: flex;
  align-items: center;
  min-height: 110px;

  & > img {
    width: 73px;
    height: 73px;
    object-fit: contain;
    margin-left: 28px;
    margin-right: 20px;
  }

  & > div {
    h1 {
      font-size: 14px;
      font-weight: bold;
      font-style: normal;
      font-stretch: normal;
      line-height: normal;
      letter-spacing: normal;
      color: var(--grey900);
    }

    h2 {
      font-size: 14px;
      font-weight: normal;
      font-style: normal;
      font-stretch: normal;
      line-height: 1.5;
      letter-spacing: normal;
      color: var(--grey700);
    }
  }
`;

const Table = styled("div")`
  display: flex;
  justify-content: space-between;

  & > div:first-child > * {
    padding-left: 28px;
  }

  & > div:last-child {
    flex-grow: unset;
  }

  & > div:last-child > * {
    padding-right: 28px;
  }

  & > div {
    flex-grow: 1;
    display: flex;
    flex-direction: column;

    & > div:first-child {
      height: 38px;
      padding-top: 11px;
      padding-bottom: 11px;
      font-size: 14px;
      font-weight: bold;
      color: var(--grey900);
      border-bottom: 1px solid var(--grey150);
      display: flex;
      align-items: center;
    }

    & > div:not(:first-child) {
      height: 18px;
      padding-top: 14px;
      padding-bottom: 14px;
      font-size: 14px;
      font-weight: normal;
      font-style: normal;
      font-stretch: normal;
      line-height: 1.29;
      letter-spacing: normal;
      color: var(--grey700);
    }
  }
`;

const ProductInformation = styled("div")`
  text-transform: capitalize;
`;

const SGFContainer = styled("div")`
  margin-top: 10px;
  background: var(--white);
`;

function StockPerGroup({ basicProduct, salesLocations, itemSgfLocations }) {
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();

  const hClick = (e) => showTooltip(e, <LocationTag type="home" showText />);
  const pClick = (e) => showTooltip(e, <LocationTag type="primary" showText />);

  return (
    <Fragment>
      <ItemContainer>
        {basicProduct.name && (
          <ItemInfo>
            <img src={basicProduct.smallImageUrl} alt={t("product2String")} />
            <ProductInformation>
              <h1>{basicProduct.name}</h1>
              <h2>{basicProduct.description}</h2>
              <h2>{basicProduct.noFormatted}</h2>
            </ProductInformation>
          </ItemInfo>
        )}
        <Table>
          <div>
            <div>
              <span>
                <Trans>salesString</Trans>
              </span>
            </div>
            {salesLocations.map(
              ({ storeLocation, isHomeBase, isHome, isPrimary }) => (
                <div key={storeLocation}>
                  {storeLocation}
                  {(isHomeBase || isHome) && <Tag onClick={hClick}>H</Tag>}
                  {isPrimary && <Tag onClick={pClick}>P</Tag>}
                </div>
              )
            )}
          </div>
          <div>
            <div>
              <span>
                <Trans>publicString</Trans>
              </span>
            </div>
            {salesLocations.map(({ locationDescription, storeLocation }) => (
              <div key={storeLocation}>{locationDescription}</div>
            ))}
          </div>
          <div>
            <div>
              <span>
                <Trans>sizeString</Trans>
              </span>
            </div>
            {salesLocations.map(({ quantity, storeLocation }) => (
              <div key={storeLocation}>{quantity}</div>
            ))}
          </div>
        </Table>
      </ItemContainer>
      {itemSgfLocations.length > 0 && (
        <SGFContainer>
          <Table>
            <div>
              <div>
                <Trans>sgfString</Trans>
              </div>
              {itemSgfLocations.map(({ location }) => (
                <div key={location}>{location}</div>
              ))}
            </div>
            <div>
              <div>
                <Trans>qtyString</Trans>
              </div>
              {itemSgfLocations.map(({ location, quantity }) => (
                <div key={location}>{quantity}</div>
              ))}
            </div>
          </Table>
        </SGFContainer>
      )}
    </Fragment>
  );
}

function StockLocations({ available, locationGroups, productsMap }) {
  return (
    <Fragment>
      <StockBadge noStock={available === 0} noInfo={"" === available}>
        <span>{textForStockItemsAmount(available)}</span>
        <span>{available}</span>
      </StockBadge>
      {locationGroups?.map((group) => {
        const short_id = parseProduct(group.productId || group.id)[0];
        const basicProduct = productsMap?.[short_id] || {};
        return (
          <StockPerGroup
            key={short_id}
            basicProduct={basicProduct}
            salesLocations={group.itemSalesLocations || []}
            itemSgfLocations={group.itemSgfLocations || []}
          />
        );
      })}
    </Fragment>
  );
}

function short_ids(list) {
  if (!list?.length) return "";
  return list.map(({ productId }) => parseProduct(productId)[0]);
}

export function PIPStockView() {
  const { full_id } = useParams();
  const { available } = useAvailableElevatedStock(full_id);
  const [short_id, type] = parseProduct(full_id);
  const { data: locationGroups } = useMFSLocations(short_id, type);
  const ids = React.useMemo(() => short_ids(locationGroups), [locationGroups]);
  const itemsInfoResult = useItemsInfoQueries(ids ?? "");

  const isLoadingItemsInfo = itemsInfoResult?.some((query) => query.isLoading);

  const itemsInfo = itemsInfoResult?.map((query) => query.data);

  const isFoodArticle = evaluateIsFoodArticle(itemsInfo?.[0]?.hfbNo);
  const productsMap = {
    [ids]: itemsInfo?.[0],
    loadingProducts: isLoadingItemsInfo,
  };
  const { mainLocations, otherLocations } = useGroupedFilteredLocations(
    full_id,
    "",
    isFoodArticle
  );

  const combinedLocations = [...mainLocations, ...otherLocations].map(
    (location) => ({
      storeLocation: location.code,
      locationDescription: location.section,
      ...location,
    })
  );

  const transformedLocations = [
    { productId: full_id, itemSalesLocations: combinedLocations },
  ];

  const { t } = useTranslation();

  const productName = itemsInfo?.[0]?.name;

  const linkbar = useMemo(() => {
    return (
      <Linkbar
        currentLabel={t("stock3String")}
        items={[
          {
            label: productName,
            to: `/product/${full_id}`,
          },
        ]}
        loading={!productName}
      />
    );
  }, [full_id, productName, t]);

  return (
    <FullScreenPopup noPadding linkbar={linkbar}>
      <StockLocations
        locationGroups={transformedLocations}
        available={available}
        productsMap={productsMap}
      />
    </FullScreenPopup>
  );
}
